import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { StaffAvatar } from '@/components/staff-avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeamProgress, type TeamMemberProgress } from './api'
import { LESSONS, PASSING_SCORE } from './content'
import { LearnHeader } from './learn-nav'
import { LessonStatusIcon, QuizScore } from './lesson-status'
import { lessonState } from './lesson-state'

function lastActive(member: TeamMemberProgress) {
  const latest = member.lessons.map((l) => l.updated_at).sort().at(-1)
  return latest ? new Date(`${latest}Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null
}

function MemberRow({ member }: { member: TeamMemberProgress }) {
  const [open, setOpen] = useState(false)
  const byKey = new Map(member.lessons.map((l) => [l.lesson_key, l]))
  const finished = LESSONS.filter((lesson) => byKey.get(lesson.key)?.completed_at).length
  const scores = member.lessons.map((l) => l.quiz_score).filter((s): s is number => s != null)
  const average = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null
  const active = lastActive(member)
  const percent = Math.round((finished / LESSONS.length) * 100)

  return (
    <li className="border-b last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none md:grid-cols-[1.25rem_minmax(0,1.4fr)_minmax(0,1fr)_6rem]"
      >
        {open ? <ChevronDown className="size-4 text-muted-foreground" /> : <ChevronRight className="size-4 text-muted-foreground" />}
        <span className="flex min-w-0 items-center gap-3">
          <StaffAvatar name={member.user.name} photoUrl={member.user.photo_url} />
          <span className="min-w-0">
            <span className="block truncate font-medium">{member.user.name}</span>
            {(member.user.title ?? member.user.role) && (
              <span className="block truncate text-xs text-muted-foreground">{member.user.title ?? member.user.role}</span>
            )}
          </span>
        </span>
        <span className="hidden min-w-0 items-center gap-3 md:flex">
          <span className="h-1.5 w-full max-w-40 overflow-hidden rounded-full bg-muted" aria-hidden="true">
            <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${percent}%` }} />
          </span>
          <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
            {finished} of {LESSONS.length}
          </span>
        </span>
        <span className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
          <span className="text-sm tabular-nums text-foreground md:hidden">
            {finished} of {LESSONS.length}
          </span>
          {average != null && (
            <span className={average >= PASSING_SCORE ? 'text-emerald-700' : 'text-amber-700'}>
              Quizzes {Math.round(average)}%
            </span>
          )}
          {active ? <span>{active}</span> : <span>Not started</span>}
        </span>
      </button>
      {open && (
        <ul className="grid gap-1 px-4 pb-3 pl-12 md:grid-cols-2">
          {LESSONS.map((lesson) => {
            const row = byKey.get(lesson.key)
            return (
              <li key={lesson.key} className="flex items-center gap-2 text-sm">
                <LessonStatusIcon state={lessonState(row)} />
                <span className="min-w-0 flex-1 truncate">{lesson.title}</span>
                <QuizScore score={row?.quiz_score} />
              </li>
            )
          })}
        </ul>
      )}
    </li>
  )
}

export function TeamProgressPage() {
  const team = useTeamProgress()
  // Most progress first, then by name, so who hasn't started is easy to spot at the bottom.
  const members = [...(team.data ?? [])].sort(
    (a, b) =>
      b.lessons.filter((l) => l.completed_at).length - a.lessons.filter((l) => l.completed_at).length ||
      a.user.name.localeCompare(b.user.name),
  )
  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <LearnHeader view="team" />
      <div className="overflow-hidden rounded-xl border bg-background">
        {team.isLoading ? (
          <div className="space-y-3 p-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : team.isError ? (
          <p className="p-4 text-sm text-red-700">Team progress couldn’t load. Refresh to try again.</p>
        ) : (
          <ul>
            {members.map((member) => (
              <MemberRow key={member.user.uuid} member={member} />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
